import { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedClass, setHighlightedStudent } from '../../store/slices/calendarSlice';
import { getRumaalColor, getClassBackgroundColor } from '../../utils/colors';
import { formatTime } from '../../utils/timeUtils';

// Readable name for a class.
const classLabel = (cls) => cls.name || `${cls.type} – ${cls.ageGroup}`;

// When/where a class runs, for the result subtitle.
const classSubLabel = (cls) => {
  const when = [cls.day, cls.startTime && formatTime(cls.startTime)]
    .filter(Boolean)
    .join(' ');
  return [cls.instructor || 'No instructor', when, cls.location]
    .filter(Boolean)
    .join(' · ');
};

const MAX_PER_GROUP = 8;

// Monotonic nonce so re-clicking the same student still re-triggers the
// scroll/highlight (a new value each click changes the Redux reference).
let highlightSeq = 0;

/**
 * Global search box for the admin header.
 *
 * Searches across classes (name, type, age group, instructor, day, location)
 * and students (by name). Selecting any result opens that class's details
 * panel. Read-only: it only reads the Redux store and opens the panel — no
 * data changes.
 */
const GlobalSearch = () => {
  const dispatch = useDispatch();
  const { classes } = useSelector((state) => state.classes);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const { classResults, studentResults } = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { classResults: [], studentResults: [] };

    const classMatches = [];
    const studentMatches = [];

    classes.forEach((cls) => {
      const haystack = [
        cls.name,
        cls.type,
        cls.ageGroup,
        cls.instructor,
        cls.day,
        cls.location,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (haystack.includes(q)) {
        classMatches.push(cls);
      }

      (cls.students || []).forEach((student) => {
        if (student.name && student.name.toLowerCase().includes(q)) {
          studentMatches.push({ student, cls });
        }
      });
    });

    return {
      classResults: classMatches.slice(0, MAX_PER_GROUP),
      studentResults: studentMatches.slice(0, MAX_PER_GROUP),
    };
  }, [query, classes]);

  const hasResults = classResults.length > 0 || studentResults.length > 0;

  // Close on outside click.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // ⌘K / Ctrl+K focuses the search from anywhere.
  useEffect(() => {
    const handleShortcut = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };
    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, []);

  const openClass = (cls, studentId = null) => {
    dispatch(setSelectedClass(cls));
    // When jumping to a specific student, flag them so the panel scrolls to
    // and highlights that row. ts ensures re-clicking the same student re-fires.
    dispatch(setHighlightedStudent(studentId ? { id: studentId, ts: ++highlightSeq } : null));
    setQuery('');
    setIsOpen(false);
  };

  const clearQuery = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  return (
    <div className="relative w-full max-w-sm" ref={containerRef}>
      {/* Search input — pill shape with focus glow */}
      <div
        className={`group relative flex items-center rounded-full border bg-gray-50/80 transition-all duration-200 ${
          isOpen
            ? 'border-orange-400 bg-white shadow-md shadow-orange-100 ring-2 ring-orange-100'
            : 'border-gray-200 hover:border-gray-300 hover:bg-white'
        }`}
      >
        <span className="pl-3.5 pr-1 text-gray-400 group-focus-within:text-orange-500 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              if (query) {
                clearQuery();
              } else {
                setIsOpen(false);
              }
            }
          }}
          placeholder="Search classes, students, instructors…"
          className="w-full bg-transparent py-2 pr-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
        />
        {query ? (
          <button
            onClick={clearQuery}
            className="mr-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            title="Clear search"
            aria-label="Clear search"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : (
          <span className="mr-3 hidden shrink-0 select-none rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-400 sm:inline">
            ⌘K
          </span>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && query.trim() && (
        <div className="absolute left-0 z-20 mt-2 w-96 max-w-[92vw] overflow-hidden rounded-2xl sm:left-auto sm:right-0 border border-gray-100 bg-white shadow-2xl shadow-gray-300/40 ring-1 ring-black/5">
          {!hasResults ? (
            <div className="px-4 py-8 text-center">
              <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-600">No matches found</p>
              <p className="text-xs text-gray-400">Nothing for “{query.trim()}”</p>
            </div>
          ) : (
            <div className="max-h-[26rem] overflow-y-auto p-2">
              {classResults.length > 0 && (
                <div className="mb-1">
                  <div className="flex items-center justify-between px-2 pb-1 pt-1.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                      Classes
                    </p>
                    <span className="rounded-full bg-gray-100 px-1.5 text-[10px] font-semibold text-gray-500">
                      {classResults.length}
                    </span>
                  </div>
                  {classResults.map((cls) => (
                    <button
                      key={cls.id || cls._id}
                      onClick={() => openClass(cls)}
                      className="group flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-orange-50"
                    >
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-gray-700 shadow-sm"
                        style={{ backgroundColor: getClassBackgroundColor(cls.type, cls.ageGroup) }}
                      >
                        {(cls.type || '?').slice(0, 2)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-gray-800">
                          {classLabel(cls)}
                        </span>
                        <span className="block truncate text-xs text-gray-500">
                          {classSubLabel(cls)}
                        </span>
                      </span>
                      <svg className="h-4 w-4 shrink-0 text-gray-300 opacity-0 transition-opacity group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}

              {studentResults.length > 0 && (
                <div className={classResults.length > 0 ? 'mt-1 border-t border-gray-100 pt-1' : ''}>
                  <div className="flex items-center justify-between px-2 pb-1 pt-1.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                      Students
                    </p>
                    <span className="rounded-full bg-gray-100 px-1.5 text-[10px] font-semibold text-gray-500">
                      {studentResults.length}
                    </span>
                  </div>
                  {studentResults.map(({ student, cls }) => (
                    <button
                      key={`${cls.id || cls._id}-${student._id || student.id || student.name}`}
                      onClick={() => openClass(cls, student._id || student.id)}
                      className="group flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-orange-50"
                    >
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold uppercase text-white shadow-sm"
                        style={{
                          backgroundColor: student.rumaalRank
                            ? getRumaalColor(student.rumaalRank)
                            : '#9CA3AF',
                          color:
                            student.rumaalRank === 'White' || student.rumaalRank === 'Yellow'
                              ? '#374151'
                              : '#fff',
                        }}
                      >
                        {(student.name || '?').slice(0, 1)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-gray-800">
                          {student.name}
                        </span>
                        <span className="block truncate text-xs text-gray-500">
                          {student.rumaalRank ? `${student.rumaalRank} · ` : ''}in {classLabel(cls)}
                        </span>
                      </span>
                      <svg className="h-4 w-4 shrink-0 text-gray-300 opacity-0 transition-opacity group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
